<template>
  <modal name="filterModal" height="auto" width="700" :scrollable="true" :clickToClose="false">
    <CloseModal :close="close" />
    <div class="fm">
      <div class="fm-title">{{ isEditing ? 'Editar' : 'Novo' }} Filtro de Consulta</div>

      <div class="row">
        <div class="col-md-6">
          <label class="fm-label">Aplicar na consulta de</label>
          <div v-if="entityReadonly" class="fm-readonly">
            <fa-icon icon="database" style="color:#3a3e81;margin-right:8px" />
            {{ entityDisplayName }}
          </div>
          <select v-else class="form-control form-control-sm" v-model="form.entity">
            <option value="">Selecione...</option>
            <option v-for="t in transformerOptions" :key="t.key" :value="t.key">{{ t.display_name }}</option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="fm-label">Tipo de filtro</label>
          <select class="form-control form-control-sm" v-model="form.filter_type" @change="onFilterTypeChange">
            <option value="odata">OData ($filter)</option>
            <option value="parameter">Parâmetro OData</option>
            <option value="post_filter">Filtro pós-transformação</option>
            <option value="sql">SQL</option>
            <option value="source">Fonte de dados (endpoint)</option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="fm-label">Ativo</label>
          <select class="form-control form-control-sm" v-model="form.is_active">
            <option :value="true">Sim</option>
            <option :value="false">Não</option>
          </select>
        </div>
      </div>

      <!-- Bloco específico: FONTE DE DADOS (source) -->
      <template v-if="form.filter_type === 'source'">
        <div class="mt-3">
          <label class="fm-label">Endpoint do ERP</label>
          <input type="text" class="form-control form-control-sm fm-input-mono" v-model="form.filter_value"
            placeholder="Ex: BusinessPartners" />
        </div>

        <div class="mt-3">
          <label class="fm-label">Filtro base (OData)</label>
          <textarea class="form-control form-control-sm fm-input-mono" rows="3" v-model="form.parameters.base_filter"
            placeholder="Ex: CardType eq 'cSupplier' and Series eq 86"></textarea>
        </div>
      </template>

      <!-- Demais tipos: chave + expressão/valor -->
      <template v-else>
        <div class="mt-3">
          <label class="fm-label">
            {{ form.filter_type === 'post_filter' ? 'Campo do registro transformado' : 'Chave do filtro' }}
          </label>
          <select v-if="form.filter_type === 'parameter'" class="form-control form-control-sm" v-model="form.filter_key">
            <option v-for="k in paramKeys" :key="k" :value="k">{{ k }}</option>
          </select>
          <input v-else type="text" class="form-control form-control-sm fm-input-mono" v-model="form.filter_key"
            :placeholder="form.filter_type === 'odata' ? '$filter' : form.filter_type === 'post_filter' ? 'Ex: filial_id, uf, ativo' : 'WHERE'" />
        </div>

        <div class="mt-3">
          <label class="fm-label">Expressão / Valor</label>
          <textarea class="form-control form-control-sm fm-input-mono" rows="3" v-model="form.filter_value" :placeholder="filterPlaceholder"></textarea>
        </div>
      </template>

      <div class="mt-3">
        <label class="fm-label">Descrição</label>
        <input type="text" class="form-control form-control-sm" v-model="form.description" placeholder="Descrição opcional do filtro" />
      </div>

      <div class="fm-help mt-3">
        <div v-if="form.filter_type === 'odata'">
          <strong>OData:</strong> Expressão adicionada ao <code>$filter</code> da chamada à API do ERP. Ex: <code>Series eq 70</code>
        </div>
        <div v-if="form.filter_type === 'parameter'">
          <strong>Parâmetro:</strong> Valor passado em parâmetros OData. Ex: <code>$top=50</code>
        </div>
        <div v-if="form.filter_type === 'post_filter'">
          <strong>Filtro pós-transformação:</strong> Filtra linhas da coleção <em>após</em> o transformer rodar,
          pelo valor de um campo do registro transformado. A entidade deve ser o <strong>transformer_key</strong>
          (ex: <code>SapB1ItemToEstoque</code>). Suporta múltiplos valores separados por vírgula.
        </div>
        <div v-if="form.filter_type === 'sql'">
          <strong>SQL:</strong> Cláusula WHERE adicional na consulta SQL.
        </div>
        <div v-if="form.filter_type === 'source'">
          <strong>Fonte de dados:</strong> Sobrescreve o endpoint padrão de busca da entidade. O <code>Endpoint do ERP</code>
          (ex: <code>BusinessPartners</code>) passa a ser usado no lugar do endpoint nativo do transformer, e o
          <code>Filtro base (OData)</code> é aplicado a essa fonte alternativa.
        </div>
      </div>

      <div class="fm-footer">
        <button class="btn btn-sm btn-secondary" @click="close">Cancelar</button>
        <button class="btn btn-sm btn-primary" @click="save" :disabled="!canSave">Salvar</button>
      </div>
    </div>
  </modal>
</template>

<script>
import { post, put } from "../../../helpers/apiRequest";
import CloseModal from "../../../components/CloseModal";

export default {
  name: "FilterModal",
  components: { CloseModal },
  props: {
    transformers: { type: Array, default: () => [] },
  },
  data() {
    return {
      isEditing: false,
      editId: null,
      entityReadonly: false,
      form: this.emptyForm(),
      paramKeys: ["$top", "$skip", "$orderby", "$select", "$expand"],
    };
  },
  computed: {
    canSave() {
      if (this.form.filter_type === "source") return !!(this.form.entity && this.form.filter_value);
      return !!(this.form.entity && this.form.filter_key && this.form.filter_value);
    },
    filterPlaceholder() {
      if (this.form.filter_type === "odata") return "Ex: Series eq 70 and CardType eq 'cCustomer'";
      if (this.form.filter_type === "sql") return "Ex: active = 1";
      if (this.form.filter_type === "post_filter") return "Ex: 02 (ou múltiplos separados por vírgula: 01,02,03)";
      return "Ex: 100";
    },
    transformerOptions() {
      if (this.transformers && this.transformers.length > 0) {
        return this.transformers.map(t => ({
          key: t.entity_type || t.transformer_key,
          display_name: t.display_name,
        }));
      }
      return [
        { key: "cliente", display_name: "Cliente" },
        { key: "produto", display_name: "Produto" },
        { key: "pedido", display_name: "Pedido" },
        { key: "transportadora", display_name: "Transportadora" },
        { key: "nota_fiscal", display_name: "Nota Fiscal" },
        { key: "boleto", display_name: "Boleto" },
        { key: "vendedor", display_name: "Vendedor" },
        { key: "filial", display_name: "Filial" },
      ];
    },
    entityDisplayName() {
      const opt = this.transformerOptions.find(t => t.key === this.form.entity);
      return opt ? opt.display_name : (this.form.entity || "—");
    },
  },
  methods: {
    emptyForm() {
      return { entity: "", filter_key: "$filter", filter_type: "odata", filter_value: "", parameters: {}, description: "", is_active: true, sort_order: 0 };
    },
    open(data) {
      if (data && data.id) {
        // Editar filtro existente
        this.isEditing = true;
        this.editId = data.id;
        this.entityReadonly = true;
        this.form = { ...data };
      } else if (data && data.entity) {
        // Novo filtro com entidade pré-definida
        this.isEditing = false;
        this.editId = null;
        this.entityReadonly = true;
        this.form = { ...this.emptyForm(), entity: data.entity };
      } else {
        // Novo filtro em branco
        this.isEditing = false;
        this.editId = null;
        this.entityReadonly = false;
        this.form = this.emptyForm();
      }
      // Garante que parameters seja sempre um objeto (binding do filtro base / source)
      if (!this.form.parameters || typeof this.form.parameters !== "object") {
        this.$set(this.form, "parameters", {});
      }
      this.$modal.show("filterModal");
    },
    close() { this.$modal.hide("filterModal"); },
    onFilterTypeChange(e) {
      const type = e.target.value;
      if (type === "parameter") this.form.filter_key = "$top";
      else if (type === "odata") this.form.filter_key = "$filter";
      else if (type === "post_filter") this.form.filter_key = "";
      else if (type === "source") {
        this.form.filter_key = "source";
        if (!this.form.parameters || typeof this.form.parameters !== "object") {
          this.$set(this.form, "parameters", {});
        }
      } else this.form.filter_key = "";
    },
    save() {
      const req = this.isEditing
        ? put(`/admin/erp-filter-configs/${this.editId}`, this.form)
        : post("/admin/erp-filter-configs", this.form);
      req.then(() => { this.$emit("saved"); this.close(); })
        .catch(err => { this.$swal && this.$swal({ title: "Erro", text: err.message, icon: "error" }); });
    },
  },
};
</script>

<style scoped>
.fm { padding: 20px; }
.fm-title { font-size: 16px; font-weight: 600; color: #1d2939; margin-bottom: 16px; }
.fm-label { font-size: 11px; font-weight: 600; color: #667085; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px; display: block; }
.fm-readonly { display: flex; align-items: center; padding: 6px 10px; font-size: 13px; color: #1f2937; font-weight: 500; background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 4px; min-height: 32px; }
.fm-input-mono { font-family: 'SF Mono', Consolas, monospace; font-size: 12.5px; }
.fm-help { background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 6px; padding: 10px 14px; font-size: 12px; color: #667085; }
.fm-help code { background: #eceaf3; padding: 1px 5px; border-radius: 3px; font-size: 11px; color: #3a3e81; }
.fm-footer { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; padding-top: 16px; border-top: 1px solid #e9ecef; }
</style>
